/** @format */

import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, makeStateKey, PLATFORM_ID, TransferState } from '@angular/core';

import { Observable } from 'rxjs';
import { map, retry } from 'rxjs/operators';

import { CustomHttpHeaders, MediaType } from '@shared/data-access/enums';
import { Breadcrumb, Chain, Environment, ProductListSEO } from '@shared/data-access/interfaces';

@Injectable({
	providedIn: 'root'
})
export class CategoryDao {
	readonly BREADCRUMBS_BY_CATEGORY_ID_STATE_KEY_PREFIX = 'breadcrumb-';
	readonly CATEGORY_NAME_BY_ID_STATE_KEY_PREFIX = 'category-';

	constructor(
		private chain: Chain,
		private environment: Environment,
		private http: HttpClient,
		@Inject(PLATFORM_ID) protected platformId: any,
		private state: TransferState
	) {}

	/**
	 * @description
	 * Fetches breadcrumbs for a given category ID
	 */
	public getBreadcrumbsByCategoryId$(categoryId: string, retries = 0): Observable<Breadcrumb[]> {
		// Get unique key
		const STATE_KEY = makeStateKey(
			`${this.BREADCRUMBS_BY_CATEGORY_ID_STATE_KEY_PREFIX}${categoryId}`
		);

		// Attempt to get the state by key
		const stateData: any = this.state.get(STATE_KEY, null);

		// Check to see if state exists
		if (stateData) {
			// Return the existing state
			return new Observable((observer) => {
				observer.next(stateData);
				observer.complete();
			});
		}

		// Query String Parameters
		const httpParams = new HttpParams().set('identifier', categoryId);

		// Standard HTTP Headers
		let httpHeaders = new HttpHeaders({
			Accept: MediaType.APPLICATION_JSON
		});

		// Custom HTTP Headers
		httpHeaders = httpHeaders.set(CustomHttpHeaders.X_PROXY_REQUEST, 'true');

		// Check platform
		if (!isPlatformBrowser(this.platformId)) {
			// Set a timeout on the server to prevent the response from taking too long
			httpHeaders = httpHeaders.set(
				CustomHttpHeaders.X_TIMEOUT,
				`${this.environment.api.spring.microServiceProxy.breadcrumb.timeout}`
			);
		}

		// HTTP Options
		const httpOptions = {
			headers: httpHeaders,
			params: httpParams
		};

		// Make the HTTP Request
		return this.http
			.get(
				`${this.environment.api.spring.microServiceProxy.breadcrumb.path}v1/store/${this.chain.storeId}/breadcrumb`,
				httpOptions
			)
			.pipe(
				map((response: any) => {
					// Breadcrumbs
					const breadcrumbs: Breadcrumb[] = response.data;

					// Set the state
					this.state.set(STATE_KEY, breadcrumbs as any);

					// Return the data
					return breadcrumbs;
				}),
				retry(retries)
			);
	}

	/**
	 * @description
	 * Gets a category name by a given ID
	 */
	public getCategoryNameByID$(
		identifier: string,
		retries = 0
	): Observable<ProductListSEO> {

		// Get unique key
		const STATE_KEY = makeStateKey<ProductListSEO>(
			`${this.CATEGORY_NAME_BY_ID_STATE_KEY_PREFIX}${identifier}`
		);

		// Get unique key for category
		const stateData: any = this.state.get(STATE_KEY, null);

		// Check to see if state exists
		if (stateData) {
			// Return the existing state
			return new Observable((observer) => {
				observer.next(stateData);
				observer.complete();
			});
		}
		// Instantiate HTTP Headers (DEFAULTS)
		let httpHeaders = new HttpHeaders({});

		// Custom HTTP Headers
		httpHeaders = httpHeaders.set(CustomHttpHeaders.X_PROXY_REQUEST, 'true');

		// Check to see if there is a timeout specified
		if (!isPlatformBrowser(this.platformId)) {
			// Set the timeout header
			httpHeaders = httpHeaders.set(
				CustomHttpHeaders.X_TIMEOUT,
				`${this.environment.api.spring.microServiceProxy.category.timeout}`
			);
		}

		const categoryUrl = `${this.environment.api.seoByChain}seo-category/v1/categories/identifier/${identifier}`;
		const httpParams = new HttpParams().set('storeId', this.chain.storeId);

		// HTTP Options
		const httpOptions = {
			headers: httpHeaders,
			params: httpParams
		};

		return this.http.get(categoryUrl, httpOptions).pipe(
			map((response: ProductListSEO) => {
				// Set the state
				this.state.set<ProductListSEO>(STATE_KEY, response);

				// Return the raw data
				return response;
			}),
			retry(retries)
		);
	}

	/**
	 * @description
	 * Gets a category by SEO Token
	 */
	public getCategoryBySeoToken$(seoToken: string, retries = 0): Observable<any> {
		// Get unique key for category
		const STATE_KEY = makeStateKey(`${this.CATEGORY_NAME_BY_ID_STATE_KEY_PREFIX}${seoToken}`);

		// Attempt to get the state by key
		const category = this.state.get(STATE_KEY, null as any);

		// Check to see if state exists
		if (category) {
			// Return the existing state
			return new Observable((observer) => {
				observer.next(category);
				observer.complete();
			});
		}

		// Instantiate HTTP Headers (DEFAULTS)
		let httpHeaders = new HttpHeaders({});

		// Custom HTTP Headers
		httpHeaders = httpHeaders.set(CustomHttpHeaders.X_PROXY_REQUEST, 'true');

		// Check to see if there is a timeout specified
		if (!isPlatformBrowser(this.platformId)) {
			// Set the timeout header
			httpHeaders = httpHeaders.set(
				CustomHttpHeaders.X_TIMEOUT,
				`${this.environment.api.spring.microServiceProxy.category.timeout}`
			);
		}

		const httpParams = new HttpParams().set('seo', seoToken);

		// HTTP Options
		const httpOptions = {
			headers: httpHeaders,
			params: httpParams
		};
		return this.http
			.get(
				`${this.environment.api.spring.microServiceProxy.category.path}v1/store/${this.chain.storeId}/categories`,
				httpOptions
			)
			.pipe(
				map((response: any) => {
					if (response && response.data && response.data[0]) {
						// Set the state
						this.state.set(STATE_KEY, response.data[0]);

						// Return the raw data
						return response.data[0];
					}

					return null;
				}),
				retry(retries)
			);
	}

	public getSubCategoriesBySeoTokens$(seoTokens: string, retries = 0): Observable<any> {
		// HTTP Headers
		// const httpHeaders = new HttpHeaders({'X-TIMEOUT': '3000'});

		const httpParams = new HttpParams().set('seo', seoTokens);

		// HTTP Options
		const httpOptions = {
			// headers: httpHeaders,
			params: httpParams
		};

		return this.http
			.get(
				`${this.environment.api.spring.microServiceProxy.category.path}v1/store/${this.chain.storeId}/categories`,
				httpOptions
			)
			.pipe(
				map((response: any) => {
					if (response && response.data) {
						// Return the raw data
						return response.data;
					}

					return null;
				}),
				retry(retries)
				// catchError(CategoryDao.handleError)
			);
	}
}
